import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import {
  ArrowLeft,
  Phone,
  AlertTriangle,
  Shield,
  TrendingUp,
  CheckCircle,
  Clock,
  HelpCircle,
  BarChart3,
  Calendar
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminEscalationTracking = ({ user }) => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showOutcomeDialog, setShowOutcomeDialog] = useState(false);
  const [outcomeStatus, setOutcomeStatus] = useState("");
  const [outcomeNotes, setOutcomeNotes] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/escalation-analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to load escalation analytics:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/dashboard");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateOutcome = async () => {
    if (!selectedIncident || !outcomeStatus) return;
    
    setIsUpdating(true);
    try {
      await axios.put(`${API}/admin/incidents/${selectedIncident.incident_id}/outcome`, {
        outcome_status: outcomeStatus,
        notes: outcomeNotes
      });
      toast.success("Outcome updated");
      setShowOutcomeDialog(false);
      setSelectedIncident(null);
      setOutcomeStatus("");
      setOutcomeNotes("");
      loadAnalytics();
    } catch (error) {
      toast.error("Failed to update outcome");
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatMonth = (monthStr) => {
    try {
      const [year, month] = monthStr.split('-');
      const date = new Date(year, parseInt(month) - 1);
      return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    } catch {
      return monthStr;
    }
  };

  const getActionBadge = (action) => {
    switch (action) {
      case "911_recommended":
        return <Badge className="bg-red-600">911 Emergency</Badge>;
      case "988_recommended":
        return <Badge className="bg-orange-500">988 Crisis Line</Badge>;
      default:
        return <Badge className="bg-yellow-500">Escalation Modal</Badge>;
    }
  };

  const getOutcomeBadge = (status) => {
    switch (status) {
      case "resolved":
        return <Badge className="bg-green-600">Resolved</Badge>;
      case "veteran_safe":
        return <Badge className="bg-blue-600">Veteran Safe</Badge>;
      case "follow_up_needed":
        return <Badge className="bg-yellow-600">Follow-up Needed</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      self_harm_ideation: "Self-Harm Ideation",
      harm_to_others: "Harm to Others",
      clinical_request: "Clinical Request",
      emergency_escalation: "Emergency Escalation",
      other_safety_concern: "Other Safety Concern"
    };
    return labels[category] || category;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading escalation data...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-background p-6">
        <Button variant="ghost" onClick={() => navigate("/admin")} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin
        </Button>
        <Card className="card-valor">
          <CardContent className="p-8 text-center">
            <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Escalation Data</h2>
            <p className="text-muted-foreground">
              Escalation tracking data will appear here when incidents occur.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { overview, outcomes, categories, monthly_trend, recent_escalations } = analytics;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  Escalation Tracking
                </h1>
                <p className="text-sm text-muted-foreground">
                  988/911 Crisis Response Outcomes
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="card-valor" data-testid="total-escalations-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <AlertTriangle className="w-8 h-8 text-amber-500" />
                <span className="text-2xl font-bold">{overview.total_escalations}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Total Escalations</p>
            </CardContent>
          </Card>

          <Card className="card-valor" data-testid="escalation-988-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Phone className="w-8 h-8 text-orange-500" />
                <span className="text-2xl font-bold">{overview.escalation_988}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">988 Referrals</p>
            </CardContent>
          </Card>

          <Card className="card-valor" data-testid="escalation-911-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <AlertTriangle className="w-8 h-8 text-red-600" />
                <span className="text-2xl font-bold">{overview.escalation_911}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">911 Emergencies</p>
            </CardContent>
          </Card>

          <Card className="card-valor" data-testid="resolved-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <CheckCircle className="w-8 h-8 text-green-500" />
                <span className="text-2xl font-bold">{outcomes.resolved + outcomes.veteran_safe}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">Resolved/Safe</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Outcome Distribution */}
          <Card className="card-valor" data-testid="outcome-distribution-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Outcome Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(outcomes).map(([status, count]) => {
                  const percentage = overview.total_escalations > 0 
                    ? (count / overview.total_escalations * 100) 
                    : 0;
                  const colors = {
                    resolved: "bg-green-500",
                    veteran_safe: "bg-blue-500",
                    follow_up_needed: "bg-yellow-500",
                    unknown: "bg-gray-400"
                  };
                  const labels = {
                    resolved: "Resolved",
                    veteran_safe: "Veteran Safe",
                    follow_up_needed: "Follow-up Needed",
                    unknown: "Pending Review"
                  };
                  return (
                    <div key={status} className="flex items-center gap-3">
                      <div className="w-28 text-sm font-medium">{labels[status]}</div>
                      <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${colors[status]} rounded-full transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground w-8 text-right">
                        {count}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Monthly Trend */}
          <Card className="card-valor" data-testid="monthly-trend-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Monthly Trend
              </CardTitle>
              <CardDescription>Escalations by type</CardDescription>
            </CardHeader>
            <CardContent>
              {monthly_trend.length > 0 ? (
                <div className="space-y-3">
                  {monthly_trend.map((month) => (
                    <div key={month.month} className="p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium">{formatMonth(month.month)}</p>
                        <span className="text-sm text-muted-foreground">
                          Total: {month["988"] + month["911"] + month.modal}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Badge className="bg-orange-500">{month["988"]} 988</Badge>
                        <Badge className="bg-red-600">{month["911"]} 911</Badge>
                        <Badge className="bg-yellow-500">{month.modal} Modal</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No monthly data available
                </p>
              )}
            </CardContent>
          </Card>

          {/* Category Breakdown */}
          <Card className="card-valor" data-testid="category-breakdown-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                Trigger Categories
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(categories).map(([category, count]) => (
                  <div key={category} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <span className="text-sm font-medium">{getCategoryLabel(category)}</span>
                    <Badge variant="outline">{count}</Badge>
                  </div>
                ))}
                {Object.keys(categories).length === 0 && (
                  <p className="text-muted-foreground text-center py-4">No data</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Escalations */}
          <Card className="card-valor" data-testid="recent-escalations-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                Recent Escalations
              </CardTitle>
              <CardDescription>Click to update outcome</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[320px]">
                {recent_escalations.length > 0 ? (
                  <div className="space-y-3 pr-4">
                    {recent_escalations.map((esc) => (
                      <div 
                        key={esc.incident_id} 
                        className="p-4 border border-border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => {
                          setSelectedIncident(esc);
                          setOutcomeStatus(esc.outcome_status || "");
                          setShowOutcomeDialog(true);
                        }}
                        data-testid={`escalation-${esc.incident_id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          {getActionBadge(esc.action_taken)}
                          {getOutcomeBadge(esc.outcome_status)}
                        </div>
                        <p className="text-sm font-medium mb-1">
                          Responder: {esc.responder_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {getCategoryLabel(esc.trigger_category)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(esc.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    No escalations recorded
                  </p>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Outcome Update Dialog */}
      <Dialog open={showOutcomeDialog} onOpenChange={setShowOutcomeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Escalation Outcome</DialogTitle>
            <DialogDescription>
              Record the outcome of this crisis escalation for tracking purposes.
            </DialogDescription>
          </DialogHeader>
          
          {selectedIncident && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2">
                {getActionBadge(selectedIncident.action_taken)}
                <span className="text-sm text-muted-foreground">
                  {formatDate(selectedIncident.created_at)}
                </span>
              </div>
              
              <div className="space-y-2">
                <Label>Outcome Status</Label>
                <Select value={outcomeStatus} onValueChange={setOutcomeStatus}>
                  <SelectTrigger data-testid="outcome-status-select">
                    <SelectValue placeholder="Select outcome" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resolved">Resolved - Crisis de-escalated</SelectItem>
                    <SelectItem value="veteran_safe">Veteran Safe - Confirmed okay</SelectItem>
                    <SelectItem value="follow_up_needed">Follow-up Needed</SelectItem>
                    <SelectItem value="unknown">Unknown - Unable to confirm</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={outcomeNotes}
                  onChange={(e) => setOutcomeNotes(e.target.value)}
                  placeholder="Add any relevant notes about this escalation..."
                  rows={3}
                  data-testid="outcome-notes-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOutcomeDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateOutcome} 
              disabled={!outcomeStatus || isUpdating}
              data-testid="save-outcome-btn"
            >
              {isUpdating ? "Saving..." : "Save Outcome"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEscalationTracking;
